import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { EventAccessRole } from '@/types';

interface UseEventPermissionResult {
  /** True if the user can write to this event (owner with non-readonly sub, or EDITOR guest). */
  canWrite: boolean;
  /** True if the user can read this event at all. False = no business being on the page. */
  canRead: boolean;
  /** The guest's role on this event, if any. */
  role: EventAccessRole | null;
  /** True if the user is the organizer (owner) of this event. */
  isOwner: boolean;
  loading: boolean;
}

interface UntypedRpcResult {
  data: boolean | null;
  error: unknown;
}

interface UntypedClient {
  rpc: (name: string, args: Record<string, unknown>) => Promise<UntypedRpcResult>;
  from: (t: string) => {
    select: (cols: string) => {
      eq: (col: string, value: unknown) => {
        maybeSingle: () => Promise<{ data: { workspace_id?: string; role?: EventAccessRole } | null; error: unknown }>;
        eq?: (col: string, value: unknown) => {
          maybeSingle: () => Promise<{ data: { role?: EventAccessRole } | null; error: unknown }>;
        };
      };
    };
  };
}

/**
 * Resolves a user's permissions on a specific event using the same SQL
 * helpers (`can_read_event` / `can_write_event`) that gate RLS — so the UI is
 * never out of sync with what the database will actually allow.
 *
 * Also surfaces `role` so consumers can show a "Solo lectura" badge.
 */
export function useEventPermission(eventId: string | undefined): UseEventPermissionResult {
  const { user, loading: authLoading } = useAuth();
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [role, setRole] = useState<EventAccessRole | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (authLoading) return;
    if (!eventId || !user?.id) {
      setCanRead(false);
      setCanWrite(false);
      setRole(null);
      setIsOwner(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = supabase as unknown as UntypedClient;

    (async () => {
      setLoading(true);

      const [readRes, writeRes, evRow] = await Promise.all([
        client.rpc('can_read_event', { p_evento_id: eventId }),
        client.rpc('can_write_event', { p_evento_id: eventId }),
        client
          .from('eventos')
          .select('workspace_id')
          .eq('id', eventId)
          .maybeSingle(),
      ]);

      if (cancelled) return;
      setCanRead(!!readRes.data);
      setCanWrite(!!writeRes.data);

      const owner = !!evRow.data?.workspace_id && evRow.data.workspace_id === user.id;
      setIsOwner(owner);

      if (!owner) {
        // Get the role for this guest+event combo so the UI can render a
        // "Solo lectura" badge.
        const accessClient = supabase as unknown as {
          from: (t: string) => {
            select: (cols: string) => {
              eq: (col: string, value: unknown) => {
                eq: (col: string, value: unknown) => {
                  maybeSingle: () => Promise<{
                    data: { role?: EventAccessRole } | null;
                    error: unknown;
                  }>;
                };
              };
            };
          };
        };
        const { data: access } = await accessClient
          .from('event_access')
          .select('role')
          .eq('evento_id', eventId)
          .eq('invited_user_id', user.id)
          .maybeSingle();
        if (cancelled) return;
        setRole(access?.role ?? null);
      } else {
        setRole(null);
      }

      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId, user?.id, authLoading]);

  return { canRead, canWrite, role, isOwner, loading };
}
